// Pure logic for the /piano-drills chord & rhythm playground: key and
// diatonic-chord data, triad voicing within a configurable keyboard range,
// rhythm patterns expanded to a 16th-note step grid, drill building, timing
// math, played-note matching for score mode, and VexFlow name conversion.
// Same posture as the other music libs here (jianpu.js, homeKeyTheory.js):
// no DOM, no audio — everything testable in plain Jest.

/* ---------- Notes & keys ---------- */

export const NOTE_NAMES_SHARP = [
  "C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B",
];
export const NOTE_NAMES_FLAT = [
  "C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B",
];

// Major keys in circle-of-fifths order for the key picker. `useFlats` drives
// note spelling everywhere (labels, sheet, VexFlow keys).
export const KEYS = [
  { name: "C", tonicPc: 0, useFlats: false },
  { name: "G", tonicPc: 7, useFlats: false },
  { name: "D", tonicPc: 2, useFlats: false },
  { name: "A", tonicPc: 9, useFlats: false },
  { name: "E", tonicPc: 4, useFlats: false },
  { name: "B", tonicPc: 11, useFlats: false },
  { name: "F#", tonicPc: 6, useFlats: false },
  { name: "Db", tonicPc: 1, useFlats: true },
  { name: "Ab", tonicPc: 8, useFlats: true },
  { name: "Eb", tonicPc: 3, useFlats: true },
  { name: "Bb", tonicPc: 10, useFlats: true },
  { name: "F", tonicPc: 5, useFlats: true },
];

export const keyByName = (name) => KEYS.find((k) => k.name === name) || null;

// MIDI 60 = C4 (middle C).
export const midiToName = (midi, useFlats = false) => {
  const names = useFlats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP;
  return `${names[((midi % 12) + 12) % 12]}${Math.floor(midi / 12) - 1}`;
};

export const isBlackKey = (midi) => [1, 3, 6, 8, 10].includes(((midi % 12) + 12) % 12);

/* ---------- Diatonic triads ---------- */

export const MAJOR_SCALE_SEMIS = [0, 2, 4, 5, 7, 9, 11];

export const DIATONIC_CHORDS = [
  { degree: 1, roman: "I", quality: "maj" },
  { degree: 2, roman: "ii", quality: "min" },
  { degree: 3, roman: "iii", quality: "min" },
  { degree: 4, roman: "IV", quality: "maj" },
  { degree: 5, roman: "V", quality: "maj" },
  { degree: 6, roman: "vi", quality: "min" },
  { degree: 7, roman: "vii°", quality: "dim" },
];

export const TRIAD_INTERVALS = { maj: [0, 4, 7], min: [0, 3, 7], dim: [0, 3, 6] };
const QUALITY_SUFFIX = { maj: "", min: "m", dim: "°" };

export const chordInfo = (degree) =>
  DIATONIC_CHORDS.find((c) => c.degree === degree) || null;

export const chordRootPc = (keyName, degree) => {
  const key = keyByName(keyName);
  if (!key || !chordInfo(degree)) return null;
  return (key.tonicPc + MAJOR_SCALE_SEMIS[degree - 1]) % 12;
};

// Display label like "C", "Dm", "B°" spelled with the key's accidentals.
export const chordLabel = (keyName, degree) => {
  const key = keyByName(keyName);
  const info = chordInfo(degree);
  if (!key || !info) return "";
  const names = key.useFlats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP;
  return `${names[chordRootPc(keyName, degree)]}${QUALITY_SUFFIX[info.quality]}`;
};

/* ---------- Voicing within a keyboard range ---------- */

// Root-position triad placed so every note fits in [low, high], choosing the
// octave whose middle sits closest to the middle of the range (ties go low).
// On wide ranges the target is clamped up to middle C so comping voicings
// don't sink into the mud at the bottom of a big keyboard.
// Returns [root, third, fifth] midis, or null if the range can't fit it.
export const voiceTriad = (keyName, degree, low, high) => {
  const rootPc = chordRootPc(keyName, degree);
  const info = chordInfo(degree);
  if (rootPc == null || !info) return null;
  const intervals = TRIAD_INTERVALS[info.quality];
  const span = intervals[2];
  const center = Math.max((low + high) / 2, 60);
  let best = null;
  for (let root = rootPc; root + span <= high; root += 12) {
    if (root < low) continue;
    const dist = Math.abs(root + span / 2 - center);
    if (!best || dist < best.dist) best = { root, dist };
  }
  if (!best) return null;
  return intervals.map((i) => best.root + i);
};

// The two right-hand voicing styles for a drill.
export const VOICINGS = [
  {
    id: "close",
    label: "Close",
    desc: "Each chord in the octave nearest the middle — least hand travel.",
  },
  {
    id: "climb",
    label: "Climbing",
    desc: "Tonic anchored just below middle C; higher degrees climb, never wrap down.",
  },
];

// Highest top note across the seven degree-chords, relative to the tonic
// root: vii° sits at +11 with a +6 diminished-fifth span.
const LADDER_SPAN = 17;

// Climbing voicing: anchor the KEY's tonic root (preferring the octave just
// below middle C, shifted by octaves so the whole I..vii° ladder fits in
// [low, high]) and build each degree ascending from it — stepping up the
// degrees never drops an octave, unlike voiceTriad's nearest-center picks.
// Null when no octave of the ladder fits the range.
export const voiceTriadAscending = (keyName, degree, low, high) => {
  const key = keyByName(keyName);
  const info = chordInfo(degree);
  if (!key || !info) return null;
  const preferred = 48 + key.tonicPc; // the tonic's last stop below middle C
  let best = null;
  for (let tonic = key.tonicPc; tonic + LADDER_SPAN <= high; tonic += 12) {
    if (tonic < low) continue;
    const dist = Math.abs(tonic - preferred);
    if (!best || dist < best.dist) best = { tonic, dist };
  }
  if (!best) return null;
  const root = best.tonic + MAJOR_SCALE_SEMIS[degree - 1];
  return TRIAD_INTERVALS[info.quality].map((i) => root + i);
};

/* ---------- Voice-leading & inversions ---------- */

const pcOf = (midi) => ((midi % 12) + 12) % 12;

// Rotate the lowest note of a (low-to-high) triad voicing up an octave,
// `inversion` times: 0 = root position, 1 = first inversion, 2 = second.
export const invertTriadVoicing = (midis, inversion) => {
  let out = [...midis];
  for (let i = 0; i < inversion; i += 1) out = [...out.slice(1), out[0] + 12];
  return out;
};

// Total voice movement between two same-size voicings: sum of |ai - bi| over
// their sorted midis. Infinity when the sizes differ (no sensible pairing).
export const voicingDistance = (a, b) => {
  if (!a || !b || a.length !== b.length) return Infinity;
  const as = [...a].sort((x, y) => x - y);
  const bs = [...b].sort((x, y) => x - y);
  return as.reduce((sum, v, i) => sum + Math.abs(v - bs[i]), 0);
};

// Root-position triad midis for the degree, in the lowest octave (root =
// its pitch class). Octave shifts of this seed every candidate voicing.
const triadBaseMidis = (keyName, degree) => {
  const rootPc = chordRootPc(keyName, degree);
  const info = chordInfo(degree);
  if (rootPc == null || !info) return null;
  return TRIAD_INTERVALS[info.quality].map((i) => rootPc + i);
};

// Every octave shift of an ascending voicing that fits entirely in
// [low, high], lowest first.
const octaveShiftsInRange = (midis, low, high) => {
  const out = [];
  const lo = midis[0];
  const hi = midis[midis.length - 1];
  for (let shift = Math.ceil((low - lo) / 12) * 12; hi + shift <= high; shift += 12) {
    out.push(midis.map((m) => m + shift));
  }
  return out;
};

// Place a voicing at the octave whose middle sits closest to the middle of
// [low, high] (clamped up to middle C, ties low) — the same placement rule
// voiceTriad uses for root position, generalized to any inversion.
const nearestCenterVoicing = (midis, low, high) => {
  const center = Math.max((low + high) / 2, 60);
  let best = null;
  octaveShiftsInRange(midis, low, high).forEach((cand) => {
    const dist = Math.abs((cand[0] + cand[cand.length - 1]) / 2 - center);
    if (!best || dist < best.dist) best = { cand, dist };
  });
  return best ? best.cand : null;
};

// A specific inversion of the degree's triad voiced inside [low, high],
// or null when it doesn't fit.
const voiceTriadInversion = (keyName, degree, inversion, low, high) => {
  const base = triadBaseMidis(keyName, degree);
  if (!base) return null;
  return nearestCenterVoicing(invertTriadVoicing(base, inversion), low, high);
};

// Smoothest next chord: among all 3 inversions of the key's diatonic triad
// on `degree`, at every octave that fits [low, high], the candidate that
// moves least (voicingDistance) from prevMidis — ties go to the lower
// voicing. Without a previous chord, falls back to the plain close voicing.
export const smoothTriad = (prevMidis, keyName, degree, low, high) => {
  if (!prevMidis || !prevMidis.length) return voiceTriad(keyName, degree, low, high);
  const base = triadBaseMidis(keyName, degree);
  if (!base) return null;
  let best = null;
  for (let inversion = 0; inversion < 3; inversion += 1) {
    for (const cand of octaveShiftsInRange(invertTriadVoicing(base, inversion), low, high)) {
      const dist = voicingDistance(prevMidis, cand);
      if (!best || dist < best.dist || (dist === best.dist && cand[0] < best.midis[0])) {
        best = { midis: cand, dist };
      }
    }
  }
  return best ? best.midis : null;
};

// Which inversion (0/1/2) of "degree's triad" a set of played midis is:
// the pitch-class set must equal the triad's exactly (doubling and octaves
// are fine), and the lowest note's pitch class decides root/1st/2nd.
// Null for anything else.
export const triadInversionOf = (midis, keyName, degree) => {
  if (!midis || midis.length < 3) return null;
  const rootPc = chordRootPc(keyName, degree);
  const info = chordInfo(degree);
  if (rootPc == null || !info) return null;
  const triadPcs = TRIAD_INTERVALS[info.quality].map((i) => (rootPc + i) % 12);
  const played = new Set(midis.map(pcOf));
  if (played.size !== 3 || !triadPcs.every((pc) => played.has(pc))) return null;
  return triadPcs.indexOf(pcOf(Math.min(...midis)));
};

// Slash-chord label for an inversion, spelled in the key: "Dm", "Dm/F",
// "Dm/A". Inversion 0 is just the plain chord label.
export const slashLabel = (keyName, degree, inversion) => {
  const key = keyByName(keyName);
  const info = chordInfo(degree);
  if (!key || !info || inversion == null) return "";
  const base = chordLabel(keyName, degree);
  if (!inversion) return base;
  const names = key.useFlats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP;
  const bassPc =
    (chordRootPc(keyName, degree) + TRIAD_INTERVALS[info.quality][inversion]) % 12;
  return `${base}/${names[bassPc]}`;
};

// How buildDrill picks each bar's inversion.
export const INVERSION_MODES = [
  { id: "roots", label: "Roots" },
  { id: "assigned", label: "Called inversions" },
  { id: "nearest", label: "Nearest voicing" },
];

/* ---------- Rhythm patterns ---------- */

// The bar is a 16-step (16th-note) grid in 4/4, like homeKeyGroove.
export const STEPS_PER_BAR = 16;

export const RHYTHMS = [
  {
    id: "straight",
    label: "Straight beat",
    desc: "The whole chord on every beat — quarter notes.",
  },
  {
    id: "half",
    label: "Half beat",
    desc: "The whole chord on every half beat — eighth notes.",
  },
  {
    id: "arpeggio",
    label: "Arpeggio",
    desc: "Broken chord, one note at a time — eighth notes.",
  },
  {
    id: "custom",
    label: "Custom (numbers)",
    desc: "Your own pattern in chord-relative numbers — like [35] 1.",
  },
];

// Order of chord tones for the arpeggio pattern (indexes into the triad).
const ARP_ORDER = [0, 1, 2, 1, 0, 1, 2, 1];

// Events for one bar of a rhythm over a voiced triad. Each event:
// { step, midis, durSteps } with step/durSteps in 16ths.
export const rhythmEvents = (rhythmId, chordMidis) => {
  if (!chordMidis || !chordMidis.length) return [];
  if (rhythmId === "straight") {
    return [0, 4, 8, 12].map((step) => ({ step, midis: [...chordMidis], durSteps: 4 }));
  }
  if (rhythmId === "half") {
    return [0, 2, 4, 6, 8, 10, 12, 14].map((step) => ({
      step,
      midis: [...chordMidis],
      durSteps: 2,
    }));
  }
  if (rhythmId === "arpeggio") {
    return ARP_ORDER.map((toneIdx, i) => ({
      step: i * 2,
      midis: [chordMidis[toneIdx]],
      durSteps: 2,
    }));
  }
  return [];
};

/* ---------- Left-hand bass ---------- */

export const BASS_PATTERNS = [
  { id: "off", label: "Off", desc: "Right hand only." },
  { id: "whole", label: "Whole note", desc: "The root, held for the bar." },
  { id: "onethree", label: "Beats 1 & 3", desc: "The root on beats 1 and 3." },
];

// The bass root under a right-hand voicing: the highest midi with the chord's
// root pitch class that sits at least a fourth below the right hand's root
// and still inside the range. Null when the range has no room for it.
export const voiceBassRoot = (keyName, degree, low, rhRootMidi) => {
  const rootPc = chordRootPc(keyName, degree);
  if (rootPc == null || rhRootMidi == null) return null;
  let best = null;
  for (let midi = rootPc; midi <= rhRootMidi - 5; midi += 12) {
    if (midi < low) continue;
    best = midi; // ascending loop ends on the highest octave that fits
  }
  return best;
};

// Events for one bar of the left hand. Same shape as rhythmEvents.
export const bassEvents = (bassId, bassMidi) => {
  if (bassMidi == null) return [];
  if (bassId === "whole") {
    return [{ step: 0, midis: [bassMidi], durSteps: 16 }];
  }
  if (bassId === "onethree") {
    return [
      { step: 0, midis: [bassMidi], durSteps: 8 },
      { step: 8, midis: [bassMidi], durSteps: 8 },
    ];
  }
  return [];
};

/* ---------- Drill building ---------- */

// A drill is a key + progression (array of degrees, one chord per bar) +
// rhythm (+ optional left-hand bass pattern), voiced into a range. Events
// carry absStep across the whole loop and hand: "rh" | "lh".
// For the "custom" rhythm the caller supplies `customEventsForBar(degree,
// chordMidis) -> rhythmEvents-shaped array` (see chordPattern.patternEvents) —
// injected as a function so this lib and chordPattern.js don't import-cycle.
//
// inversionModeId (see INVERSION_MODES):
//   "roots"    — every bar in root position, exactly the classic behavior.
//   "assigned" — each bar gets a random inversion 0–2 (drawn from `rng`),
//                voiced near the middle of the range; labels go slash-style.
//   "nearest"  — bar 1 as usual (close/climb per voicingId), then each bar
//                is smoothTriad-chained off the previous bar's voicing.
// In every mode the left hand keeps playing the plain root (the pop-comping
// texture: bass anchors roots while the right hand moves).
//
// progression: an explicit array of degrees that overrides `degrees` —
// bar i plays progression[i % length], order preserved, and the bar count
// rounds max(degrees.length, one cycle) up to a whole number of cycles.
export const buildDrill = (
  keyName,
  degrees,
  rhythmId,
  low,
  high,
  bassId = "off",
  customEventsForBar = null,
  voicingId = "close",
  inversionModeId = "roots",
  progression = null,
  rng = Math.random
) => {
  const key = keyByName(keyName);
  if (!key) return { bars: [], events: [], totalSteps: 0, error: "Unknown key." };
  if (rhythmId === "custom" && typeof customEventsForBar !== "function") {
    return { bars: [], events: [], totalSteps: 0, error: "Write a custom pattern first." };
  }
  const bassOn = bassId !== "off";
  // With a bass line, reserve the bottom of the range for the left hand so
  // the right-hand voicings leave room underneath (but never squeeze the
  // right hand below an octave-and-a-fifth of candidates).
  const rhLow = bassOn ? Math.max(low, Math.min(low + 12, high - 19)) : low;
  let barDegrees = degrees;
  if (Array.isArray(progression) && progression.length) {
    const minBars = Math.max(degrees ? degrees.length : 0, progression.length);
    const cycles = Math.ceil(minBars / progression.length);
    barDegrees = [];
    for (let c = 0; c < cycles; c += 1) barDegrees.push(...progression);
  }
  const bars = [];
  let prevMidis = null;
  for (const degree of barDegrees) {
    const info = chordInfo(degree);
    let midis = null;
    let inversion = 0;
    if (info) {
      if (inversionModeId === "assigned") {
        // Draw an inversion; when it can't be voiced in the range (a first
        // inversion can top out of a 13-key window), fall to the next one
        // that fits so a narrow range never errors at random. The bar's
        // `inversion` is always the one actually voiced.
        const drawn = Math.floor(rng() * 3);
        for (let tryIdx = 0; tryIdx < 3 && !midis; tryIdx += 1) {
          inversion = (drawn + tryIdx) % 3;
          midis = voiceTriadInversion(keyName, degree, inversion, rhLow, high);
        }
      } else if (inversionModeId === "nearest" && prevMidis) {
        midis = smoothTriad(prevMidis, keyName, degree, rhLow, high);
        inversion = midis ? triadInversionOf(midis, keyName, degree) ?? 0 : 0;
      } else {
        midis =
          voicingId === "climb"
            ? voiceTriadAscending(keyName, degree, rhLow, high)
            : voiceTriad(keyName, degree, rhLow, high);
      }
    }
    if (!info || !midis) {
      return {
        bars: [],
        events: [],
        totalSteps: 0,
        error: "The keyboard range is too small to fit these chords — widen it.",
      };
    }
    prevMidis = midis;
    const bassMidi = bassOn ? voiceBassRoot(keyName, degree, low, midis[0]) : null;
    if (bassOn && bassMidi == null) {
      return {
        bars: [],
        events: [],
        totalSteps: 0,
        error:
          "No room for the bass line under these chords — widen the low end of the range or turn bass off.",
      };
    }
    bars.push({
      degree,
      roman: info.roman,
      label: slashLabel(keyName, degree, inversion),
      inversion,
      midis,
      bassMidi,
    });
  }
  const events = [];
  bars.forEach((bar, barIdx) => {
    bassEvents(bassId, bar.bassMidi).forEach((e) => {
      events.push({ ...e, hand: "lh", bar: barIdx, absStep: barIdx * STEPS_PER_BAR + e.step });
    });
    // Custom patterns are chord-relative (1 = the chord's ROOT), and
    // chordPattern.patternEvents reads the root from chordMidis[0] — so for
    // inverted bars hand it the voiced midis rotated root-first, keeping the
    // pattern rooted right while it tracks the voicing's register.
    const patternMidis =
      bar.inversion === 0
        ? bar.midis
        : (() => {
            const rootPc = chordRootPc(keyName, bar.degree);
            const idx = bar.midis.findIndex((m) => pcOf(m) === rootPc);
            if (idx <= 0) return bar.midis;
            return [bar.midis[idx], ...bar.midis.slice(0, idx), ...bar.midis.slice(idx + 1)];
          })();
    const rhEvents =
      rhythmId === "custom"
        ? customEventsForBar(bar.degree, patternMidis)
        : rhythmEvents(rhythmId, bar.midis);
    rhEvents.forEach((e) => {
      events.push({ ...e, hand: "rh", bar: barIdx, absStep: barIdx * STEPS_PER_BAR + e.step });
    });
  });
  // Keep events non-decreasing in absStep (eventIndexAtStep early-breaks on
  // it); Array.sort is stable, so lh stays ahead of rh on shared onsets.
  events.sort((a, b) => a.absStep - b.absStep);
  return {
    keyName,
    useFlats: key.useFlats,
    rhythmId,
    bassId,
    low,
    high,
    inversionModeId,
    bars,
    events,
    totalSteps: bars.length * STEPS_PER_BAR,
    error: null,
  };
};

// Indices of the events sounding at absStep — the latest onset per hand, so
// a held whole-note bass keeps "sounding" while right-hand chords change.
export const soundingEventIndices = (drill, absStep) => {
  if (!drill || !drill.events.length || absStep < 0) return [];
  const latestByHand = {};
  drill.events.forEach((event, index) => {
    if (event.absStep <= absStep) latestByHand[event.hand || "rh"] = index;
  });
  return Object.values(latestByHand);
};

// Index of the event sounding at absStep (the latest event at or before it),
// or -1 before the first event / for an empty drill.
export const eventIndexAtStep = (drill, absStep) => {
  if (!drill || !drill.events.length) return -1;
  let idx = -1;
  for (let i = 0; i < drill.events.length; i += 1) {
    if (drill.events[i].absStep <= absStep) idx = i;
    else break;
  }
  return idx;
};

/* ---------- Timing & score-mode matching ---------- */

// Duration of one 16th-note step: a beat is 60000/bpm ms, a step a quarter of it.
export const stepMs = (bpm) => 15000 / bpm;

export const DEFAULT_TOLERANCE_MS = 150;

// Match a played note (posMs into the current loop) against the drill's
// expected events, tolerant of the loop boundary. Returns the closest
// { index, deltaMs } whose event contains `midi`, or null (a wrong note).
export const matchNote = (drill, bpm, posMs, midi, tolMs = DEFAULT_TOLERANCE_MS) => {
  if (!drill || !drill.events.length) return null;
  const totalMs = drill.totalSteps * stepMs(bpm);
  let best = null;
  drill.events.forEach((event, index) => {
    if (!event.midis.includes(midi)) return;
    const t = event.absStep * stepMs(bpm);
    [posMs - t, posMs - t - totalMs, posMs - t + totalMs].forEach((deltaMs) => {
      if (Math.abs(deltaMs) > tolMs) return;
      if (!best || Math.abs(deltaMs) < Math.abs(best.deltaMs)) best = { index, deltaMs };
    });
  });
  return best;
};

/* ---------- Range & keyboard helpers ---------- */

export const RANGE_MIN = 24; // C1
export const RANGE_MAX = 96; // C7
export const MIN_RANGE_SPAN = 12; // at least an octave
export const DEFAULT_RANGE = { low: 36, high: 72 }; // C2–C5 — room for a bass line

// Options for the low/high pickers: every note in the supported range.
export const rangeNoteOptions = (useFlats = false) => {
  const out = [];
  for (let midi = RANGE_MIN; midi <= RANGE_MAX; midi += 1) {
    out.push({ midi, name: midiToName(midi, useFlats) });
  }
  return out;
};

// Keep low/high ordered and at least an octave apart, inside the bounds.
export const clampRange = (low, high) => {
  let lo = Math.max(RANGE_MIN, Math.min(low, RANGE_MAX - MIN_RANGE_SPAN));
  let hi = Math.min(RANGE_MAX, Math.max(high, lo + MIN_RANGE_SPAN));
  if (hi - lo < MIN_RANGE_SPAN) lo = hi - MIN_RANGE_SPAN;
  return { low: lo, high: hi };
};

// Computer-keyboard note entry: the classic a–k row covers one octave from an
// anchor C. Pick the C that keeps the octave inside the range, nearest its
// middle; fall back to the low end when no full octave from a C fits.
export const KB_KEYS = [
  { offset: 0, key: "a" },
  { offset: 1, key: "w" },
  { offset: 2, key: "s" },
  { offset: 3, key: "e" },
  { offset: 4, key: "d" },
  { offset: 5, key: "f" },
  { offset: 6, key: "t" },
  { offset: 7, key: "g" },
  { offset: 8, key: "y" },
  { offset: 9, key: "h" },
  { offset: 10, key: "u" },
  { offset: 11, key: "j" },
  { offset: 12, key: "k" },
];

export const kbAnchor = (low, high) => {
  const center = (low + high) / 2 - 6;
  let best = null;
  for (let midi = low; midi + 12 <= high; midi += 1) {
    if (midi % 12 !== 0) continue;
    const dist = Math.abs(midi - center);
    // Ties go to the higher C, matching synth-lab's middle-C octave.
    if (!best || dist <= best.dist) best = { midi, dist };
  }
  return best ? best.midi : low;
};

/* ---------- Progressions ---------- */

// Cap for the click-to-add chord builder in the UI. A progression preset or
// parsed progression may run longer (the 12-bar blues does) — buildDrill
// always plays whole cycles.
export const MAX_BARS = 8;

export const PROGRESSION_PRESETS = [
  { id: "pop", label: "I–V–vi–IV", degrees: [1, 5, 6, 4] },
  { id: "fifties", label: "I–vi–IV–V", degrees: [1, 6, 4, 5] },
  { id: "jazz", label: "ii–V–I", degrees: [2, 5, 1] },
  {
    id: "blues",
    label: "12-bar blues",
    degrees: [1, 1, 1, 1, 4, 4, 1, 1, 5, 4, 1, 1],
  },
];

export const MAX_PROGRESSION_CHORDS = 16;

// Parse a typed progression like "1 5 6 4" or "2-5-1": scale degrees 1–7
// separated by spaces, commas, or dashes. Returns { degrees, error } —
// friendly error strings in the chordPattern.parsePattern style.
export const parseProgression = (text) => {
  const fail = (error) => ({ degrees: [], error });
  const raw = (text || "")
    .trim()
    .split(/[\s,–—-]+/)
    .filter(Boolean);
  if (!raw.length) return fail("Type a progression — like 1 5 6 4.");
  const degrees = [];
  for (const word of raw) {
    if (!/^[1-7]$/.test(word)) {
      return fail(`Can't read "${word}" — chords are scale degrees 1–7.`);
    }
    degrees.push(Number(word));
  }
  if (degrees.length > MAX_PROGRESSION_CHORDS) {
    return fail(
      `That's ${degrees.length} chords — ${MAX_PROGRESSION_CHORDS} is the most a drill can loop.`
    );
  }
  return { degrees, error: null };
};

/* ---------- "Stay in the pocket" drill helpers ---------- */

// A 13-key window around middle C: G3–G4. Every diatonic triad has at least
// one inversion that fits any 13-key window (asserted in tests).
export const POCKET_WINDOW = { low: 55, high: 67 };

// Every voicing (all 3 inversions × all octave shifts) of the degree's triad
// with every note inside [low, high] inclusive, deduped.
export const voicingsInWindow = (keyName, degree, low, high) => {
  const base = triadBaseMidis(keyName, degree);
  if (!base) return [];
  const out = [];
  const seen = new Set();
  for (let inversion = 0; inversion < 3; inversion += 1) {
    octaveShiftsInRange(invertTriadVoicing(base, inversion), low, high).forEach((cand) => {
      const id = cand.join(",");
      if (!seen.has(id)) {
        seen.add(id);
        out.push(cand);
      }
    });
  }
  return out;
};

// Judge a played chord against the pocket window. ok when the pitch-class
// set matches the degree's triad (doubling fine) AND every note sits inside
// [low, high]; outOfWindow separates right-chord-wrong-place (inversion
// still reported) from wrong notes (inversion null).
export const checkPocketChord = (playedMidis, keyName, degree, low, high) => {
  const inversion = triadInversionOf(playedMidis, keyName, degree);
  if (inversion == null) return { ok: false, inversion: null, outOfWindow: false };
  if (!playedMidis.every((m) => m >= low && m <= high)) {
    return { ok: false, inversion, outOfWindow: true };
  }
  return { ok: true, inversion, outOfWindow: false };
};

// Reduce played midis to one note per pitch class (the lowest), sorted —
// the simple 3-note reduction pocketFeedback measures motion with.
const lowestPerPc = (midis) => {
  const byPc = new Map();
  [...midis]
    .sort((a, b) => a - b)
    .forEach((m) => {
      const pc = pcOf(m);
      if (!byPc.has(pc)) byPc.set(pc, m);
    });
  return [...byPc.values()].sort((a, b) => a - b);
};

// Voice-leading feedback for the pocket drill: how far the player actually
// moved from the previous chord, and the best they could have done inside
// the window — so the UI can say "you moved 9; Dm/F was 2 away".
// With no previous chord there's nothing to measure: moved 0, best null.
export const pocketFeedback = (playedMidis, prevMidis, keyName, degree, low, high) => {
  if (!prevMidis || !prevMidis.length) {
    return { moved: 0, bestMoved: null, bestLabel: null };
  }
  const moved = voicingDistance(prevMidis, lowestPerPc(playedMidis || []));
  let best = null;
  voicingsInWindow(keyName, degree, low, high).forEach((cand) => {
    const dist = voicingDistance(prevMidis, cand);
    if (!best || dist < best.dist || (dist === best.dist && cand[0] < best.midis[0])) {
      best = { midis: cand, dist };
    }
  });
  return {
    moved,
    bestMoved: best ? best.dist : null,
    bestLabel: best
      ? slashLabel(keyName, degree, triadInversionOf(best.midis, keyName, degree))
      : null,
  };
};

/* ---------- VexFlow conversion ---------- */

// VexFlow key string like "c#/4" or "eb/3".
export const midiToVexKey = (midi, useFlats = false) => {
  const names = useFlats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP;
  const name = names[((midi % 12) + 12) % 12].toLowerCase();
  return `${name}/${Math.floor(midi / 12) - 1}`;
};

// Dotted values ("hd", "qd", "8d") come from custom patterns; VexFlow parses
// the "d" suffix for ticks, and SheetView attaches the dot glyph.
export const DUR_TO_VEX = { 16: "w", 12: "hd", 8: "h", 6: "qd", 4: "q", 3: "8d", 2: "8", 1: "16" };

// Pick a clef for a set of midis. Treble unless the notes genuinely live in
// the bass register (average below A3) — voicings straddling middle C read
// far better on a treble staff than floating above a bass one.
export const clefForMidis = (midis) => {
  if (!midis.length) return "treble";
  const avg = midis.reduce((a, b) => a + b, 0) / midis.length;
  return avg < 57 ? "bass" : "treble";
};
